import type { FC } from 'react'
import { useState, useEffect } from 'react'
import cn from 'classnames'
import { useCart } from 'hooks/use-cart'
import { Button, Modal, Input } from 'components/ui'
import { MapMarker } from 'components/icons'
import { AddressService } from 'modules/address/services'
import { useForm } from 'react-hook-form'
import { useRouter } from 'next/router'
import { parseCookies, setCookie } from 'nookies'
import { ROUTE_PRODUCT_INDEX } from 'routes/products'
import { isPedidoFacilSession } from 'modules/pedido-facil/utils/pedido-facil-session'
import type { DeliveryAreaProps } from '.'

import s from './delivery-area.module.css'

const DeliveryArea: FC<DeliveryAreaProps> = ({ className, ...rest }) => {
  const router = useRouter()
  const addressService = new AddressService()
  const { zipCode, setZipCode, setPopup, popup, emptyCart } = useCart()

  const [open, setOpen] = useState<boolean>(false)

  const cookies = parseCookies()
  const storedCity = (cookies.city as never) || ''

  const [needsRefresh, setNeedsRefresh] = useState<boolean>(false)

  const {
    register,
    handleSubmit,
    formState: { errors, touched, isSubmitting },
    getValues,
    setError,
  } = useForm({
    mode: 'onChange',
  })

  const handlePopup = (value: boolean) => {
    setPopup(value)
    setOpen(value)
  }

  useEffect(() => {
    async function syncZipCode() {
      if(!zipCode) {
        setCookie(null, 'city', '', {
          maxAge: 30 * 24 * 60 * 60,
          path: '/',
        })
        setCookie(null, 'zipcode', '', {
          maxAge: 30 * 24 * 60 * 60,
          path: '/',
        })
      } else {
        const { data } = await addressService.getAddressByZipcode(zipCode)
        setCookie(null, 'city', data.city_name, {
          maxAge: 30 * 24 * 60 * 60,
          path: '/',
        })
        setCookie(null, 'zipcode', zipCode, {
          maxAge: 30 * 24 * 60 * 60,
          path: '/',
        })
      }
    }

    syncZipCode();

  }, [zipCode, cookies.city])

  useEffect(() => {
    setOpen(popup)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popup])

  useEffect(() => {
    if (zipCode) {
      setOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function checkZipCode(value: string) {
    // try {
    //   const response = await addressService.validateDeliveryArea(value)
    //   // @ts-ignore
    //   const nextShippingType = response?.data?.delivery_area ? 'local' : 'nacional'

    //   if (storedShippingType !== nextShippingType) {
    setNeedsRefresh(true)
    //   }

    //   setShippingType(response?.data?.delivery_area ? 'local' : 'nacional')
    // } catch (e) {
    //   //
    // } finally {
    setZipCode(value)
    // }
  }

  function refresh() {
    router.reload()
  }

  async function onSubmit() {
    const value = getValues().zipCode.replace(/\D/g, '')
    try {
      const { data } = await addressService.getAddressByZipcode(value)
      setCookie(null, 'city', data.city_name, {
        maxAge: 30 * 24 * 60 * 60,
        path: '/',
      })
      setCookie(null, 'zipcode', value, {
        maxAge: 30 * 24 * 60 * 60,
        path: '/',
      })
      checkZipCode(value)
      localStorage.removeItem('selectedAddress')
      router.push(isPedidoFacilSession(router.pathname) ? '/pedido-facil' : '/');
    } catch (e) {
      setError('zipCode', { message: 'CEP não encontrado' })
    }
  }

  return (
    <>
      <Button variant="ghost" className={cn(s.root, className)} {...rest} onClick={() => handlePopup(!open)}>
        <MapMarker /> <span>{storedCity || 'Informe seu CEP'}</span>
      </Button>
      <Modal onClose={() => handlePopup(!open)} open={open} showCloseButton className={s.modal_delivery}>
        <div className={s.delivery_area}>
          <header>
            <div className={s.title}>
              {router.asPath.includes('/produto/') ? (
                <>
                  Informe seu CEP para verificar <br />
                  se esse produto é entregue no seu endereço pelo produtor
                </>
              ) : (
                <>
                  Informe seu CEP para facilitar <br />
                  sua busca de produtores que atendem seu endereço
                </>
              )}
            </div>
            <div className={s.subTitle}>Informe o CEP abaixo</div>
          </header>
          <form onSubmit={handleSubmit(onSubmit)} className={s.form}>
            <div className={s.input}>
              <Input
                type="phone"
                name="zipCode"
                placeholder="CEP"
                error={errors?.zipCode?.message}
                touched={touched?.zipCode}
                disabled={isSubmitting}
                defaultValue={zipCode}
                maxLength={8}
                ref={register({
                  required: {
                    value: true,
                    message: 'Informe o CEP',
                  },
                  minLength: {
                    value: 8,
                    message: 'CEP inválido',
                  },
                })}
              />
            </div>
            <Button type="submit" disabled={isSubmitting} variant="primary" loading={isSubmitting} className={s.button}>
              Verificar
            </Button>
          </form>
          {needsRefresh && !router.asPath.includes('/produto/') && router.asPath !== '/' ? (
            <p className={s.refresh}>
              Clique{' '}
              <Button type="button" variant="text" onClick={refresh}>
                aqui
              </Button>{' '}
              para atualizar a página.
            </p>
          ) : (
            <></>
          )}

          {needsRefresh && router.asPath === '/' ? (
            <p className={s.refresh}>
              Clique{' '}
              <Button
                href={`${ROUTE_PRODUCT_INDEX}?zipcode=${zipCode}`}
                type="button"
                variant="text"
                onClick={() => handlePopup(false)}
              >
                aqui
              </Button>{' '}
              para ver os produtos que atendem sua região.
            </p>
          ) : (
            <></>
          )}

          {/* <div className={s.footer}>
          <Button variant="ghost" onClick={() => setPopup(!popup)}>
            deixar para depois
          </Button>
        </div> */}
        </div>
      </Modal>
    </>
  )
}

export default DeliveryArea
